/*
 * ******************************************************************
 * Copyright (c) 2021-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Component, EventEmitter, Input, Output } from "@angular/core";
import { BaseDialog } from "../base-dialog";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";
import { TaskStatesEnum, TaskV2 } from "@api/generated/vcdr";
import { ErrorMessageType } from "@shared/components/loading-error/error-msg.component";
import { RefreshEvent } from "@app/services/dispatcher/events";

// @formatter:off
@Component({
    standalone: false,
    selector: 'confirmation-dialog',
    templateUrl: 'confirmation-dialog.html',
    styleUrls: ['confirmation-dialog.scss']
})
// @formatter:on
export class ConfirmationDialog extends BaseDialog {
    public ConfirmationDialogType = ConfirmationDialogType;

    @Input() title: string;
    @Input() message: string;
    @Input() warningMessage: string;
    @Input() confirmationBtnText: string = "dialog.button.Yes";
    @Input() cancelBtnText: string = "dialog.button.No";
    @Input() confirmationBtnStyle: string = "btn-primary";
    @Input() type: ConfirmationDialogType = ConfirmationDialogType.NotSpecified;
    @Input() customIcon: string;
    @Input() conditions: string[] = [];
    @Input() executeOperation: Observable<TaskV2>;
    @Input() validateOperation: Observable<any>;
    @Input() helpInfoLabel: string = "console.error.helpInfo.label";
    @Output() outputTask: EventEmitter<TaskV2> = new EventEmitter<TaskV2>();

    public confirmedConditions: ConfirmationCondition[];
    public valid: boolean = false;
    public errorMsgType: ErrorMessageType = ErrorMessageType.ERROR;
    public helpInfoLbl: string = "";
    public helpInfoUrl: string = "";
    public submitting: boolean = false;

    public init(): void {
        this.confirmedConditions = [];
        if (this.conditions?.length) {
            this.conditions.forEach(condition => {
                const confirmCondition = new ConfirmationCondition();
                confirmCondition.text = condition;
                confirmCondition.confirmed = false;
                this.confirmedConditions.push(confirmCondition);
            });
        }
        this.validate();
    }

    public disableSubmitButton(): boolean {
        if (!this.valid) {
            return true;
        }
        if (!this.confirmedConditions?.length) {
            return false;
        }
        return !!this.confirmedConditions.find(condition => !condition.confirmed);
    }

    public submit(): void {
        if (!this.executeOperation) {
            this.submitting = false;
            this.outputTask.emit(null);
            this.closeDialog(true);
        } else {
            this.submitting = true;
            this.executeOperation
                .pipe(take(1))
                .subscribe({
                               next: (task) => {
                                   this.submitting = false;

                                   if (task?.state === TaskStatesEnum.FAILED) {
                                       this.errorMsg = this.errorService.getUiErrorMessage(
                                           null,
                                           "console.error.execution.unknown",
                                           "console.error.execution.http");
                                       if (task?.data?.errors?.length > 0) {
                                           const errorMsgs = task.data.errors.map(err => err.error_message);
                                           console.warn(JSON.stringify(errorMsgs));
                                       }
                                   } else {
                                       if (task) {
                                           this.outputTask.emit(task);
                                       }
                                       this.dispatcher.publishEvent(new RefreshEvent());
                                       this.closeDialog(true);
                                   }
                               },
                               error: (error) => {
                                   this.submitting = false;
                                   this.errorMsg = this.errorService.getUiErrorMessage(
                                       error,
                                       "console.error.execution.unknown",
                                       "console.error.execution.http");
                                   console.warn(this.errorMsg);
                               }
                           });
        }
    }

    private validate(): void {
        if (!this.validateOperation) {
            this.valid = true;
        } else {
            this.valid = false;
            this.validateOperation
                .pipe(take(1))
                .subscribe({
                               next: (result) => {
                                   this.valid = result === true || result !== false && (!result || result.valid === true);
                                   if (!this.valid) {
                                       if (result?.error) {
                                           this.errorMsg = result.error;
                                       } else {
                                           this.errorMsg = this.l10nService.getMessage("console.error.validation.msg");
                                       }
                                   }
                               },
                               error: (error) => {
                                   if (this.errorService.needHelpInfo(error)) {
                                       this.errorMsgType = ErrorMessageType.HELP_INFO;
                                       this.helpInfoLbl = this.l10nService.getMessage(this.helpInfoLabel);
                                       this.helpInfoUrl = this.errorService.getHelpInfoUrl(error);
                                   }
                                   this.errorMsg = this.errorService.getUiErrorMessage(
                                       error,
                                       "console.error.validation.unknown",
                                       "console.error.validation.http");
                                   console.warn(this.errorMsg);
                               }
                           });
        }
    }
}

export enum ConfirmationDialogType {
    NotSpecified,
    Info,
    Question,
    Error,
    Warning
}

class ConfirmationCondition {
    text: string;
    confirmed: boolean;
}
